import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

// Styles for the Contact Section
const ContactSection = styled.section`
  padding: 2rem 1.5rem; // Adjusted for better mobile view
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  overflow: hidden;
  margin-bottom: 1rem; // Ensure minimal gap at the bottom
  @media (max-width: 600px) {
    padding: 1rem; // Less padding on smaller screens
  }
`;

// Header and Paragraph Styling
const ContactHeader = styled.h2`
  font-size: 2rem; // Adjusted for better mobile view
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.accent};
  position: relative;
  display: inline-block;
  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 3px; // Adjusted height
    background-color: ${({ theme }) => theme.accent};
    bottom: -8px; // Adjusted bottom position
    left: 0;
    animation: underline 1s infinite alternate;
  }
  @keyframes underline {
    from { width: 0; }
    to { width: 100%; }
  }
`;

const ContactParagraph = styled(motion.p)`
  font-size: 1rem; // Adjusted for better mobile view
  margin-bottom: 1rem; // Reduced bottom margin
  opacity: 0.8;
`;

// Contact Form and its Fields
const ContactForm = styled(motion.form)`
  display: flex;
  flex-direction: column;
  gap: 0.75rem; // Reduced gap
  max-width: 500px;
  margin: 0 auto;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

// Input and TextArea with Animated Focus Effect
const Input = styled(motion.input)`
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.color};
  border-radius: 4px;
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.color};
  outline: none;
  font-size: 0.9rem; // Adjusted font size
  @media (max-width: 600px) {
    font-size: 0.8rem; // Smaller font size on mobile
  }
  &:focus {
    border-color: ${({ theme }) => theme.accent};
    box-shadow: 0 0 10px ${({ theme }) => theme.accent};
  }
`;

const TextArea = styled(motion.textarea)`
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.color};
  border-radius: 4px;
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.color};
  outline: none;
  font-size: 0.9rem; // Adjusted font size
  @media (max-width: 600px) {
    font-size: 0.8rem; // Smaller font size on mobile
  }
  &:focus {
    border-color: ${({ theme }) => theme.accent};
    box-shadow: 0 0 10px ${({ theme }) => theme.accent};
  }
`;

// Submit Button with Hover Effect
const SubmitButton = styled(motion.button)`
  padding: 0.75rem;
  border: none;
  border-radius: 4px;
  background: ${({ theme }) => theme.accent};
  color: ${({ theme }) => theme.background};
  font-size: 0.9rem; // Adjusted font size
  cursor: pointer;
  @media (max-width: 600px) {
    font-size: 0.8rem; // Smaller font size on mobile
  }
  &:hover {
    background: ${({ theme }) => theme.color};
    color: ${({ theme }) => theme.accent};
    transform: scale(1.05);
  }
`;

// Back to Top Button Styling
const BackToTopButton = styled(motion.button)`
  position: fixed;
  bottom: 20px; // Adjusted position
  right: 20px; // Adjusted position
  padding: 0.75rem;
  background-color: ${({ theme }) => theme.accent};
  color: ${({ theme }) => theme.background};
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
  font-size: 1.5rem;
  box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.3);
  @media (max-width: 600px) {
    font-size: 1.2rem; // Smaller font size on mobile
    bottom: 15px; // Adjusted position
    right: 15px; // Adjusted position
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [showButton, setShowButton] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const mailtoLink = `mailto:dhamaharsh9@gmail.com?subject=Message from ${formData.name}&body=${encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\nMessage: ${formData.message}`
    )}`;
    window.location.href = mailtoLink;
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.pageYOffset > 300) setShowButton(true);
      else setShowButton(false);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <ContactSection>
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <ContactHeader>Contact Me</ContactHeader>
        <ContactParagraph
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          Feel free to get in touch with me through the contact form below:
        </ContactParagraph>
        <ContactForm
          onSubmit={handleSubmit}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <FormField>
            <Input
              type="text"
              name="name"
              placeholder="Your Name"
              value={formData.name}
              onChange={handleChange}
              required
              whileFocus={{ scale: 1.05 }}
            />
          </FormField>
          <FormField>
            <Input
              type="email"
              name="email"
              placeholder="Your Email"
              value={formData.email}
              onChange={handleChange}
              required
              whileFocus={{ scale: 1.05 }}
            />
          </FormField>
          <FormField>
            <TextArea
              name="message"
              rows="4"
              placeholder="Your Message"
              value={formData.message}
              onChange={handleChange}
              required
              whileFocus={{ scale: 1.05 }}
            />
          </FormField>
          <SubmitButton type="submit" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
            Send Message
          </SubmitButton>
        </ContactForm>
      </motion.div>

      {/* Back to Top Button */}
      <BackToTopButton
        isVisible={showButton}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={scrollToTop}
      >
        ↑
      </BackToTopButton>
    </ContactSection>
  );
};

export default Contact;
